import http from "@/utils/axios";
import { notify } from "@kyvg/vue3-notification";
import {
  getFromLocalStorage,
  getFromSessionStorage,
  removeFromLocalStorage,
  removeFromSessionStorage,
  saveToLocalStorage,
  saveToSessionStorage,
} from "@/utils/localstorage";

export default {
  namespaced: true,
  state: {
    cart: getFromLocalStorage("cart", []),
    visitServices: getFromSessionStorage("visit_services", []),
    selectedExamination: null,
    additionalServices: getFromLocalStorage("additional_services", null),
    errors: [],
    totalPrice: 0,
    savings: 0,
    ownPackageSavings: 0,
    patientDiscount: 0,
    patientDiscountPercent: 0,
    mobile_nursing_service: getFromSessionStorage(
      "mobile_nursing_service",
      null
    ),
    covid_nursing_service: getFromSessionStorage("covid_nursing_service", null),
    nursing_service: getFromSessionStorage("nursing_service", null),
    selectedCity: getFromSessionStorage("selected_city", null),
    homeService: getFromSessionStorage("home_service", false),
    selectedFacility: getFromSessionStorage("selected_facility", null),
    isSelectedBundles: false,
    isSelectedOwnPackages: false,
    discountCode: getFromSessionStorage("discount_code", null),
  },
  getters: {
    getNursingService: (state) => state.nursing_service,
    getMobileNursingService: (state) => state.mobile_nursing_service,
    getCart: (state) => state.cart,
    getCartIds: (state) => state.cart.map((item) => item.id),
    getDiscount: (state) => (patientId) => {
      const examCount = state.cart.filter(
        (examination) =>
          examination.patients.includes(patientId) &&
          examination.for_own_package &&
          !examination.added_by_doctor
      ).length;
      let discount = 0;

      if (examCount < 5) return discount;
      if (examCount >= 5) discount = 5;
      if (examCount > 15) discount = 25;

      if (examCount > 5 && examCount <= 15) {
        discount += 2 * (examCount - 5);
      }

      return discount;
    },
    getDiscountForAll: (state) => {
      const examCount = state.cart.filter(
        (examination) =>
          examination.for_own_package && !examination.added_by_doctor
      ).length;
      let discount = 0;

      if (examCount < 5) return discount;
      if (examCount >= 5) discount = 5;
      if (examCount > 15) discount = 25;

      if (examCount > 5 && examCount <= 15) {
        discount += 2 * (examCount - 5);
      }

      return discount;
    },
    getNextDiscount: (state) => (patientId) => {
      const examCount = state.cart.filter(
        (examination) =>
          examination.patients.includes(patientId) &&
          examination.for_own_package &&
          !examination.added_by_doctor
      ).length;

      let discount = 5;
      let quantity = null;

      if (examCount < 5) {
        return {
          discount,
          quantity: 5 - examCount,
        };
      }

      quantity = 1;
      discount += 2 * (examCount - 5) + 2;

      if (discount > 25) {
        return null;
      }

      return {
        discount,
        quantity,
      };
    },
    getNextDiscountAll: (state) => {
      const examCount = state.cart.filter(
        (examination) =>
          examination.for_own_package && !examination.added_by_doctor
      ).length;

      let discount = 5;
      let quantity = null;

      if (examCount < 5) {
        return {
          discount,
          quantity: 5 - examCount,
        };
      }

      quantity = 1;
      discount += 2 * (examCount - 5) + 2;

      if (discount > 25) {
        return null;
      }

      return {
        discount,
        quantity,
      };
    },
    getExaminations: (state) => {
      return state.cart.filter(
        (examination) =>
          !examination.added_by_doctor && !examination.for_own_package
      );
    },
    getExaminationsAddedByDoctor: (state) => {
      return state.cart.filter((examination) => examination.added_by_doctor);
    },
    getPatietntExaminations: (state) => (patientId) => {
      return state.cart.filter(
        (examination) =>
          examination.patients.includes(patientId) &&
          !examination.added_by_doctor &&
          !examination.for_own_package
      );
    },
    getPatietntExaminationsAddedByDoctor: (state) => (patientId) =>
      state.cart.filter(
        (examination) =>
          examination.patients.includes(patientId) &&
          examination.added_by_doctor &&
          !examination.for_own_package
      ),
    getOwnPackageExaminations: (state) =>
      state.cart.filter(
        (examination) =>
          examination.for_own_package && !examination.added_by_doctor
      ),
    getPatientOwnPackageExaminations: (state) => (patientId) =>
      state.cart.filter(
        (examination) =>
          examination.patients.includes(patientId) &&
          examination.for_own_package
      ),
    getPatientVisitServices: (state) => (patientId) =>
      state.visitServices.filter((examination) =>
        examination.patients.includes(patientId)
      ),
    getExaminationIds: (state) => state.cart.map((item) => item.id),
    getExaminationPatients: (state) => (examinationId) =>
      state.cart.find((examinaiton) => examinaiton.id === examinationId)
        ?.patients ?? [],
    getExaminationAddedByDoctor: (state) => (id) => {
      const item = state.cart.find((item) => item.id === id);
      if (typeof item !== "undefined") {
        return item.added_by_doctor;
      }
      return 0;
    },
    getPatientHasExamination: (state) => (examinationId, patientId) => {
      return (
        state.cart.filter(
          (examinaiton) =>
            examinaiton.id === examinationId &&
            examinaiton.patients.includes(patientId)
        ).length > 0
      );
    },
    getErrors: (state) => state.errors,
    getTotalPrice: (state) => state.totalPrice,
    getSavings: (state) => state.savings,
    getOwnPackageSavings: (state) => state.ownPackageSavings,
    getCartAddedByDoctor: (state) =>
      state.cart.filter((item) => item.added_by_doctor),
    getAdditionalServices: (state) => state.additionalServices,
    getSelectedCity: (state) => state.selectedCity,
    getSelectedCityZone: (state) => state.selectedCity?.zone_id,
    getSelectedExamination: (state) => state.selectedExamination,
    hasExaminations: (state) => state.cart.length,
    existExaminationId: (state) => (id) =>
      state.cart.findIndex((examination) => examination.id === id) !== -1,
    isHomeService: (state) => state.homeService,
    getIsSelectedBundles: (state) => state.isSelectedBundles,
    getIsSelectedOwnPackages: (state) => state.isSelectedOwnPackages,
    getSelectedFacility: (state) => state.selectedFacility,
    isSelectedCityOrPoint: (state) =>
      (state.homeService && state.selectedCity != null) ||
      (!state.homeService && state.selectedFacility != null),
    getVisitServices: (state) => state.visitServices,
    getVisitServicesIds: (state) => state.visitServices.map((item) => item.id),
    getDiscountCode: (state) => state.discountCode,
    getDiscountedProducts: (state) =>
      state.discountCode?.productsDiscounts ?? [],
    requireAdditionalServices(state) {
      const hasIncludedNursing = state.cart.find(
        (item) => item.is_including_nursing
      );

      if (hasIncludedNursing) {
        return false;
      }

      return (
        state.cart.filter((item) => item.require_nursing_services).length > 0
      );
    },
  },
  mutations: {
    setCart(store, cart) {
      store.cart = cart;
    },
    setVisitServices(store, visitServices) {
      store.visitServices = visitServices;
    },
    setTotal(store, total) {
      store.totalPrice = total;
    },
    setSavings(store, amount) {
      store.savings = amount;
    },
    setOwnPackageSavings(store, amount) {
      store.ownPackageSavings = amount;
    },
    setCartItem(store, { index, item }) {
      store.cart[index] = { ...item };
    },
    setSelectedExamination(state, examination) {
      state.selectedExamination = { ...examination };
    },
    clearSelectedExamination(state) {
      state.selectedExamination = null;
    },
    setNursingServices(state, services) {
      state.covid_nursing_service = services[0];
      state.nursing_service = services[1];
      state.mobile_nursing_service = services[2];
      saveToSessionStorage("covid_nursing_service", services[0]);
      saveToSessionStorage("nursing_service", services[1]);
      saveToSessionStorage("mobile_nursing_service", services[2]);
    },
    setAdditionalServices(state, services) {
      state.additionalServices = services;
      saveToLocalStorage("additional_services", state.additionalServices);
    },
    clearCart(store) {
      store.cart = [];
    },
    clearAll(state) {
      state.cart = [];
      state.totalPrice = 0;
      state.errors = [];
      state.additionalServices = null;
      state.visitServices = [];
      state.discountCode = null;

      removeFromLocalStorage("cart");
      removeFromLocalStorage("additional_services");
      removeFromSessionStorage("covid_nursing_service");
      removeFromSessionStorage("nursing_service");
      removeFromSessionStorage("visit_services");
      removeFromSessionStorage("discount_code");
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    clearErrors(state) {
      state.errors = [];
    },
    setPatientDiscountPercent(state, patientDiscountPercent) {
      state.patientDiscountPercent = patientDiscountPercent;
    },
    setHomeService(state, homeService) {
      state.homeService = homeService;
      saveToSessionStorage("home_service", state.homeService);
    },
    setSelectedCity(state, city) {
      state.selectedCity = city;
      saveToSessionStorage("selected_city", state.selectedCity);
    },
    setSelectedFacility(state, facility) {
      state.selectedFacility = facility;
      saveToSessionStorage("selected_facility", state.selectedFacility);
    },
    removeFromCart(state, patientId) {
      state.cart = state.cart.map((examination) => {
        examination.patients = examination.patients.filter(
          (id) => id !== patientId
        );
        return examination;
      });
    },
    clearEmptyCartExaminations(state) {
      state.cart = state.cart.filter(
        (examination) => examination.patients.length > 0
      );
    },
    setDiscountCode(state, discountCode) {
      state.discountCode = discountCode;
      saveToSessionStorage("discount_code", state.discountCode);
    },
  },
  actions: {
    calculatePrice({ state, getters, commit }) {
      let totalPrice = 0;
      let totalPriceNoDiscount = 0;
      let ownPackageSavings = 0;
      let examinations = getters.getExaminations.filter(
        (exam) => exam.available
      );

      totalPrice += examinations.reduce(
        (accumulator, current) => accumulator + current.price * 1,
        0
      );
      totalPriceNoDiscount += examinations.reduce(
        (accumulator, current) => accumulator + current.price * 1,
        0
      );

      let doctorExaminations = getters.getExaminationsAddedByDoctor;
      totalPrice += doctorExaminations.reduce(
        (accumulator, current) => accumulator + current.price * 1,
        0
      );
      totalPriceNoDiscount += doctorExaminations.reduce(
        (accumulator, current) => accumulator + current.price * 1,
        0
      );

      let ownPackageExams = getters.getOwnPackageExaminations;
      ownPackageExams = ownPackageExams.filter((exam) => exam.available);
      const discount = getters.getDiscountForAll;

      totalPrice += ownPackageExams.reduce((accumulator, current) => {
        let ownPackageDiscountValue = (current.price * discount) / 100;
        ownPackageSavings += ownPackageDiscountValue;

        if (discount > 0) {
          return accumulator + current.price - ownPackageDiscountValue;
        } else {
          return accumulator + current.price * 1;
        }
      }, 0);
      totalPriceNoDiscount += ownPackageExams.reduce((accumulator, current) => {
        return accumulator + current.price * 1;
      }, 0);

      let additionalPrice = 0;

      if (
        state.additionalServices?.name === "UPIEL" &&
        totalPrice !== 0 &&
        state.nursing_service?.price
      ) {
        additionalPrice += state.nursing_service?.price;
      }

      if (state.homeService && state.cart.length > 0 && totalPrice !== 0) {
        additionalPrice += state.mobile_nursing_service.price * 1;
      }

      if (state.visitServices.length > 0) {
        totalPrice = state.visitServices.reduce(
          (total, service) => total + service.price,
          totalPrice
        );

        totalPriceNoDiscount = state.visitServices.reduce(
          (total, service) => total + service.price,
          totalPriceNoDiscount
        );
      }

      if (state.discountCode) {
        totalPrice -= state.discountCode.discount;
      }

      commit("setTotal", totalPrice + additionalPrice);
      commit("setSavings", totalPriceNoDiscount - totalPrice);
      commit("setOwnPackageSavings", ownPackageSavings);
    },
    updateSelectedCity({ commit, dispatch, rootGetters }, city) {
      commit("setSelectedCity", city);

      if (rootGetters["features/isEnabled"]("workbeep_visits")) {
        dispatch("checkout/checkZone", null, { root: true });
      }
    },
    setVisitService({ commit }, visitService) {
      commit("setSelectedVisitService", visitService);
    },
    initAdditionalServices({ getters, commit, rootGetters }) {
      if (!getters.requireAdditionalServices) {
        commit("setAdditionalServices", null);
        return;
      }

      const patients = rootGetters["checkout/getPatients"];
      let patientsWithExaminations = patients.filter((patient) => {
        return (
          getters.getPatientOwnPackageExaminations(patient.id).length > 0 ||
          getters.getPatietntExaminations(patient.id).length > 0
        );
      }).length;

      commit("setAdditionalServices", {
        quantity: patientsWithExaminations >= 1 ? patientsWithExaminations : 1,
        name: "UPIEL",
      });
    },
    removePatientFromExaminations({ state, commit, dispatch }, patientId) {
      commit("removeFromCart", patientId);
      commit("clearEmptyCartExaminations");
      dispatch("initAdditionalServices");
      dispatch("calculatePrice");
      saveToLocalStorage("cart", state.cart);
    },
    assignCartToPatients({ state, commit, dispatch }, patients) {
      state.cart.forEach((item, index) => {
        commit("setCartItem", { index, item: { ...item, patients } });
      });

      saveToLocalStorage("cart", state.cart);
      commit("setCart", state.cart);
      dispatch("initAdditionalServices");
      dispatch("calculatePrice");
    },
    assignVisitServicesToPatients({ state, commit, dispatch }, patients) {
      const tmp = [];

      state.visitServices.forEach((visitService) => {
        tmp.push({
          ...visitService,
          patients,
        });
      });

      commit("setVisitServices", tmp);
      saveToSessionStorage("visit_services", state.visitServices);
      dispatch("calculatePrice");
    },
    removeItemFromCart({ commit, dispatch }, itemId) {
      let cart = getFromLocalStorage("cart", []);
      const itemIndex = cart.findIndex((item) => item.id === itemId);

      if (itemIndex >= 0) {
        cart.splice(itemIndex, 1);
        saveToLocalStorage("cart", cart);
        commit("setCart", cart);
        dispatch("initAdditionalServices");
        dispatch("calculatePrice");
      }
    },
    removeVisitService({ commit, dispatch }, itemId) {
      let visitServices = getFromSessionStorage("visit_services", []);
      const itemIndex = visitServices.findIndex((item) => item.id === itemId);

      if (itemIndex >= 0) {
        visitServices.splice(itemIndex, 1);
        saveToSessionStorage("visit_services", visitServices);
        commit("setVisitServices", visitServices);
        dispatch("calculatePrice");
      }
    },
    manageVisitServices({ commit, dispatch }, { service, patients }) {
      let visitServices = getFromSessionStorage("visit_services", []);
      const index = visitServices.findIndex((item) => item.id === service?.id);

      if (patients.length === 0 && index !== -1) {
        visitServices.splice(index, 1);
      } else if (index === -1) {
        visitServices = [
          {
            id: service.id,
            main_medical_category_id: service.main_medical_category_id,
            name: service.name,
            price: service.price,
            time: service.time,
            patients,
            available: true,
            added_by_doctor: service?.added_by_doctor ?? false,
          },
          ...visitServices,
        ];
      } else {
        visitServices[index] = { ...visitServices[index], patients };
      }
      saveToSessionStorage("visit_services", visitServices);
      commit("setVisitServices", visitServices);
      dispatch("calculatePrice");
    },
    manageCart({ commit, dispatch }, { examination, patients }) {
      let cart = getFromLocalStorage("cart", []);
      const index = cart.findIndex((item) => item.id === examination?.id);

      if (patients.length === 0 && index !== -1) {
        cart.splice(index, 1);
      } else if (index === -1) {
        cart = [
          {
            id: examination.id,
            service_id: examination.service_id,
            name: examination.name,
            price: examination.price,
            package: examination.package,
            is_including_nursing: examination.is_including_nursing,
            require_nursing_services: examination.require_nursing_services,
            require_covid_nursing_services:
              examination.require_covid_nursing_services,
            for_own_package: examination.for_own_package,
            own_package_type: examination.own_package_type,
            slug: examination.slug,
            patients,
            available: true,
            added_by_doctor: examination?.added_by_doctor ?? false,
            is_screening: examination.is_screening,
            is_weight_loss_package: examination.is_weight_loss_package,
            screening_type: examination.screening_type,
            basic_service_id: examination.basic_service_id,
            extended_service_id: examination.extended_service_id,
            screening_service: examination.screening_service,
          },
          ...cart,
        ];
      } else {
        cart[index] = { ...cart[index], patients };
      }
      saveToLocalStorage("cart", cart);

      commit("setCart", cart);
      dispatch("initAdditionalServices");
      dispatch("calculatePrice");
    },
    async fetchUpdatedExaminationsPrices(state, payload) {
      return await http.post("updated/examinations-data", payload);
    },
    async updateIsHomeService({ commit, dispatch, getters }, payload) {
      commit("setHomeService", payload);

      if (getters.isSelectedCityOrPoint) {
        await dispatch("updateExaminationsPrices");
      }
    },
    reAssignExamination({ state, commit }, { oldPatientId, newPatientId }) {
      const tmpCart = [];

      state.cart.forEach((examination) => {
        let patients = examination.patients;

        if (examination.patients.includes(oldPatientId)) {
          patients = examination.patients.filter((id) => id !== oldPatientId);
          patients.push(newPatientId);
        }

        tmpCart.push({
          ...examination,
          patients,
        });
      });

      commit("setCart", tmpCart);
      saveToLocalStorage("cart", state.cart);
    },
    reAssignVisitServices({ state, commit }, { oldPatientId, newPatientId }) {
      const tmp = [];

      state.visitServices.forEach((visitService) => {
        let patients = visitService.patients;

        if (visitService.patients.includes(oldPatientId)) {
          patients = visitService.patients.filter((id) => id !== oldPatientId);
        }
        patients.push(newPatientId);

        tmp.push({
          ...visitService,
          patients,
        });
      });

      commit("setVisitServices", tmp);
      saveToSessionStorage("visit_services", state.visitServices);
    },
    async updateExaminationsPrices({ getters, dispatch }) {
      const { isHomeService } = getters;

      const { data } = await dispatch("fetchUpdatedExaminationsPrices", {
        examinaitons: getters.getExaminationIds,
        ...(isHomeService && { city_uuid: getters.getSelectedCity?.uuid }),
        ...(!isHomeService && { facility_id: getters.getSelectedFacility?.id }),
      });

      dispatch("updateCartItemsPrices", data);
    },
    updateCartItemsPrices({ state, commit }, data) {
      const tmpCart = [];
      state.cart.forEach((examinaiton) => {
        const newExamination = data.examinations[examinaiton.id];

        if (newExamination) {
          tmpCart.push({
            id: newExamination.id,
            service_id: examinaiton.service_id,
            name: examinaiton.name,
            price: newExamination.price,
            package: examinaiton.package,
            is_including_nursing: examinaiton.is_including_nursing,
            require_nursing_services: examinaiton.require_nursing_services,
            require_covid_nursing_services:
              examinaiton.require_covid_nursing_services,
            slug: examinaiton.slug,
            patients: examinaiton.patients,
            for_own_package: examinaiton.for_own_package,
            own_package_type: examinaiton.own_package_type,
            available: newExamination.isAvailable,
            added_by_doctor: examinaiton.added_by_doctor,
            is_screening: examinaiton.is_screening,
            is_weight_loss_package: examinaiton.is_weight_loss_package,
            screening_type: examinaiton.screening_type,
            basic_service_id: examinaiton.basic_service_id,
            extended_service_id: examinaiton.extended_service_id,
            screening_service: examinaiton.screening_service,
          });
        }
      });
      commit("setCart", tmpCart);
      saveToLocalStorage("cart", state.cart);
    },
    setUserDefaultFacility({ commit }, facility) {
      commit("setSelectedFacility", facility);
    },
    removeDoctorExaminations({ getters, dispatch }) {
      const addedByDoctor = [...getters.getCartAddedByDoctor];

      addedByDoctor.forEach((exam) => {
        dispatch("manageCart", {
          examination: exam,
          patients: "",
        });
      });
    },
    addReferralExaminations({ dispatch }, items) {
      items.forEach((exam) => {
        dispatch("manageCart", {
          examination: exam,
          patients: "",
        });
      });
    },
    async realizeDiscountCode(
      { rootGetters, getters, commit, dispatch },
      discount_code
    ) {
      return new Promise((resolve, reject) => {
        return http
          .post("/check-code", {
            discount_code,
            examinations: getters["getCartIds"],
            visitServices: getters["getVisitServicesIds"],
            nursingService: {
              price:
                rootGetters["examinations/getNursingServices"]?.nursing_service
                  ?.price,
              quantity: getters["getAdditionalServices"]?.quantity,
            },
          })
          .then(({ data }) => {
            commit("setDiscountCode", { ...data });
            dispatch("calculatePrice");
            resolve(data);
          })
          .catch(() => {
            commit("setDiscountCode", null);
            notify({
              type: "danger",
              text: "The promotional code used is no longer available",
            });
          });
      });
    },
    async checkOrderAutoDiscountCode(
      { rootGetters, getters, commit, dispatch },
      discount_code
    ) {
      return new Promise((resolve, reject) => {
        return http
          .post("/check-order", {
            discount_code,
            examinations: getters["getCartIds"],
            visitServices: getters["getVisitServicesIds"],
            nursingService: {
              price:
                rootGetters["examinations/getNursingServices"]?.nursing_service
                  ?.price,
              quantity: getters["getAdditionalServices"]?.quantity,
            },
          })
          .then(({ data }) => {
            if (data.code) {
              commit("setDiscountCode", { ...data });

              notify({
                type: "success",
                text: "Promo code automatically applied",
              });
            }

            dispatch("calculatePrice");

            resolve(data);
          })
          .catch((error) => {
            console.error(error?.message);
          });
      });
    },
  },
};
